import { Badge } from '@/components/ui/badge';
import InjectionManifestChips from './InjectionManifestChips';
import LlmCallDetail from './LlmCallDetail';

function JsonBlock({ data }) {
  if (data == null) return null;
  return (
    <pre className="text-[10px] font-mono text-zinc-700 bg-white/60 p-2 rounded-sm mt-0.5 overflow-auto max-h-40 whitespace-pre-wrap">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function PromptPreview({ text, label = 'Prompt preview' }) {
  if (!text) return null;
  return (
    <div className="col-span-2 mt-2">
      <span className="text-[9px] font-mono text-zinc-400 uppercase">{label}</span>
      <pre className="text-[10px] font-mono text-zinc-700 bg-white/60 p-2 rounded-sm mt-0.5 overflow-auto max-h-48 whitespace-pre-wrap">{text}</pre>
    </div>
  );
}

function GenericFields({ ev, exclude = [] }) {
  const skip = new Set([
    '_id', 'tenant_id', 'timestamp', 'event_type', 'phase', 'description',
    'conversation_id', 'injection_manifest', 'classification_prompt_sent',
    'system_prompt_preview', 'prompt_preview', ...exclude,
  ]);
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
      {Object.entries(ev).filter(([k]) => !skip.has(k)).map(([key, value]) => {
        if (value === null || value === undefined || value === '') return null;
        const isLong = typeof value === 'string' && value.length > 80;
        const isObj = typeof value === 'object';
        return (
          <div key={key} className={isLong || isObj ? 'col-span-2' : ''}>
            <span className="text-[9px] font-mono text-zinc-400 uppercase">{key.replace(/_/g, ' ')}</span>
            {isObj ? <JsonBlock data={value} /> : isLong ? (
              <pre className="text-[10px] font-mono text-zinc-700 bg-white/60 p-2 rounded-sm mt-0.5 overflow-auto max-h-40 whitespace-pre-wrap">{String(value)}</pre>
            ) : (
              <p className="text-[10px] font-mono text-zinc-700">{String(value)}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function EventDetailRenderer({ ev }) {
  const t = ev.event_type;
  const manifest = ev.injection_manifest;

  if (t === 'classification') {
    return (
      <div>
        <InjectionManifestChips manifest={manifest} />
        <div className="grid grid-cols-2 gap-2 mt-2 text-[10px]">
          <div><span className="text-zinc-400">Intent:</span> {ev.intent}</div>
          <div><span className="text-zinc-400">Agente:</span> {ev.suggested_agent}</div>
          <div><span className="text-zinc-400">Prioridad:</span> {ev.priority}</div>
          <div><span className="text-zinc-400">Ticket:</span> {String(ev.create_ticket)}</div>
          <div className="col-span-2"><span className="text-zinc-400">Razon:</span> {ev.reasoning}</div>
        </div>
        <PromptPreview text={manifest?.prompt_preview || ev.classification_prompt_sent} />
        <GenericFields ev={ev} exclude={['intent', 'priority', 'category', 'suggested_agent', 'label', 'create_ticket', 'confidence', 'reasoning', 'classification_result', 'model_used', 'classifier_agent']} />
      </div>
    );
  }

  /*
   * Los tres eventos de memoria. **El caso que hay que poder leer de un vistazo es el
   * negativo**: que la memoria entre se nota en la respuesta, que no entre no se nota en
   * ningun lado, y el motivo es lo unico que distingue "el agente no la uso" de "el agente no
   * la tenia".
   */
  /*
   * Que tarea creo el agente y **a quien la quiso poner**. Lo segundo es lo que no se veia:
   * cuando la plantilla no reparte a criterio, la tarea aparece asignada a otra persona y la
   * eleccion del agente no quedaba en ningun lado visible.
   */
  if (t === 'tarea_creada_por_un_agente') {
    return (
      <div>
        <Badge className={`text-[9px] mb-2 ${ev.eleccion_ignorada ? 'bg-amber-100 text-amber-900' : 'bg-zinc-100 text-zinc-700'}`}>
          {ev.eleccion_ignorada ? 'Eleccion del agente ignorada' : 'Asignada'}
        </Badge>
        <p className="text-[11px] text-zinc-700">{ev.titulo}</p>
        {ev.eleccion_ignorada ? (
          <p className="text-[10px] text-zinc-600 mt-1">
            El agente eligio <b>{ev.elegido_por_el_agente}</b> · quedo en <b>{ev.asignado || 'sin dueño'}</b>
            <span className="block text-zinc-400">
              La plantilla «{ev.plantilla || '—'}» no le deja repartir a criterio.
            </span>
          </p>
        ) : (
          <p className="text-[10px] text-zinc-600 mt-1">
            Asignada a <b>{ev.asignado || 'sin dueño'}</b>
            {ev.reparto_del_agente ? ' · la plantilla le deja elegir' : ''}
          </p>
        )}
        <p className="text-[10px] text-zinc-400 mt-1">
          tipo: {ev.task_type || '—'} · caso: {ev.ticket_id}
        </p>
      </div>
    );
  }

  if (t === 'memoria_de_persona_leida') {
    return (
      <div>
        <Badge className={`text-[9px] mb-2 ${ev.entro ? 'bg-purple-100 text-purple-900' : 'bg-zinc-200 text-zinc-700'}`}>
          {ev.entro ? `Entro al prompt · ${ev.caracteres} car.` : 'No entro'}
        </Badge>
        {!ev.entro && <p className="text-[10px] text-zinc-600 mt-1">Motivo: {ev.motivo}</p>}
        {!!ev.aviso && <p className="text-[10px] text-amber-700 mt-1">⚠ {ev.aviso}</p>}
        <p className="text-[10px] text-zinc-400 mt-1">persona: {ev.contact_id || '—'}</p>
      </div>
    );
  }

  if (t === 'memoria_de_persona_actualizada') {
    const corrio = ev.resultado !== 'no_corresponde';
    return (
      <div>
        <Badge className={`text-[9px] mb-2 ${ev.hechos_nuevos ? 'bg-green-100 text-green-900' : 'bg-zinc-200 text-zinc-700'}`}>
          {corrio ? `${ev.hechos_nuevos} hecho(s) nuevo(s)` : 'No corresponde'}
        </Badge>
        {!corrio && <p className="text-[10px] text-zinc-600 mt-1">Motivo: {ev.motivo}</p>}
        {corrio && (
          <p className="text-[10px] text-zinc-600 mt-1">
            Total de hechos: {ev.hechos_totales}
            {ev.regenerada
              ? ` · el parrafo se reescribio (${ev.motivo_regeneracion})`
              : ' · el parrafo no se reescribio todavia'}
          </p>
        )}
        <p className="text-[10px] text-zinc-400 mt-1">persona: {ev.contact_id || '—'} · caso: {ev.ticket_id}</p>
      </div>
    );
  }

  if (t === 'memoria_organizacional_actualizada') {
    const fallo = ev.resultado === 'fallo';
    return (
      <div>
        <Badge className={`text-[9px] mb-2 ${fallo ? 'bg-red-100 text-red-900' : (ev.event_id ? 'bg-green-100 text-green-900' : 'bg-zinc-200 text-zinc-700')}`}>
          {fallo ? 'Fallo la extraccion' : (ev.event_id ? 'Borrador creado' : 'No corresponde')}
        </Badge>
        {fallo && <p className="text-[10px] text-red-700 mt-1">{ev.error}</p>}
        {!!ev.event_id && (
          <p className="text-[10px] text-zinc-600 mt-1">
            Lo escribio: <b>{ev.extraction || 'desconocido'}</b>
            {ev.confidence != null ? ` · confianza ${ev.confidence}` : ''}
          </p>
        )}
        <p className="text-[10px] text-zinc-400 mt-1">caso: {ev.ticket_id} · {ev.event_id || '—'}</p>
      </div>
    );
  }

  if (t === 'kb_gate_decision') {
    return (
      <div>
        <Badge className={`text-[9px] mb-2 ${ev.retrieve_kb ? 'bg-purple-100 text-purple-900' : 'bg-zinc-200 text-zinc-700'}`}>
          {ev.fail_open ? 'Fail-open → RAG' : (ev.retrieve_kb ? 'Consultar KB' : 'Omitir RAG')}
        </Badge>
        <InjectionManifestChips manifest={manifest} />
        <p className="text-[10px] text-zinc-600 mt-1">{ev.reason}</p>
        <PromptPreview text={manifest?.prompt_preview} label="System prompt gate" />
        <PromptPreview text={manifest?.user_prompt_preview} label="User prompt gate" />
      </div>
    );
  }

  if (t === 'coordinator_post_turn') {
    const tools = ev.tools_used || [];
    return (
      <div>
        {ev.skipped && <Badge className="text-[9px] mb-2 bg-zinc-100">Omitido: {ev.reason}</Badge>}
        <InjectionManifestChips manifest={manifest} />
        <p className="text-[10px] text-zinc-600 mt-1">Rondas: {ev.tool_rounds} · run_id: {ev.run_id}</p>
        {tools.length > 0 && (
          <table className="w-full text-[10px] mt-2 border border-zinc-200">
            <thead><tr className="bg-zinc-50"><th className="text-left p-1">Tool</th><th className="text-left p-1">OK</th></tr></thead>
            <tbody>
              {tools.map((row, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="p-1 font-mono">{row.tool}</td>
                  <td className="p-1">{String(row.success)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <PromptPreview text={ev.final_text_preview} label="Texto final" />
        <PromptPreview text={manifest?.prompt_preview} />
      </div>
    );
  }

  if (t === 'llm_call') {
    return <LlmCallDetail ev={ev} />;
  }

  if (t === 'tool_execution') {
    const skipped = ev.payload?.skipped_duplicate || ev.skipped_duplicate;
    return (
      <div>
        {skipped && (
          <Badge className="text-[9px] mb-2 bg-amber-100 text-amber-900">
            Omitido: {ev.payload?.skipped_reason || ev.skipped_reason || 'duplicado'}
          </Badge>
        )}
        {ev.payload?.llm_request_id && (
          <p className="text-[9px] text-zinc-400 mb-1 font-mono">llm_request: {ev.payload.llm_request_id.slice(0, 8)} · ronda {ev.payload.llm_round}</p>
        )}
        <GenericFields ev={ev.payload ? { ...ev, ...ev.payload } : ev} />
      </div>
    );
  }

  if (t === 'agent_processing') {
    return (
      <div>
        <InjectionManifestChips manifest={manifest} />
        <div className="grid grid-cols-2 gap-2 mt-2 text-[10px]">
          <div><span className="text-zinc-400">Habilidades:</span> {(ev.skills_enabled || []).join(', ') || '—'}</div>
          <div><span className="text-zinc-400">RAG:</span> {ev.rag_sources_count} fuentes</div>
        </div>
        {ev.runtime_policy && <JsonBlock data={ev.runtime_policy} />}
        <PromptPreview text={ev.system_prompt_preview || manifest?.prompt_preview} label="System prompt" />
        <PromptPreview text={ev.user_prompt_sent} label="User message" />
      </div>
    );
  }

  if (t === 'rag_search' && ev.skipped_by_gate) {
    return (
      <div>
        <Badge className="text-[9px] mb-2 bg-purple-50 text-purple-800">RAG omitido por gate KB</Badge>
        <p className="text-[10px] text-zinc-600">{ev.gate_reason}</p>
        {ev.gate_correlation_ts && (
          <p className="text-[9px] text-zinc-400 mt-1">Correlacion gate: {ev.gate_correlation_ts}</p>
        )}
        <GenericFields ev={ev} exclude={['skipped_by_gate', 'gate_reason', 'gate_correlation_ts']} />
      </div>
    );
  }

  if (typeof t === 'string' && t.startsWith('heartbeat')) {
    const payload = ev.payload || {};
    const delivery = payload.delivery || ev.delivery || {};
    const eff = payload.effective_config || {};
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {payload.action && <Badge className="text-[9px] bg-teal-50 text-teal-800">{payload.action}</Badge>}
          {payload.forced_after_silence && <Badge className="text-[9px] bg-amber-50 text-amber-800">forzado tras silencios</Badge>}
          {payload.delivered === true && <Badge className="text-[9px] bg-green-50 text-green-800">entregado</Badge>}
          {payload.delivered === false && <Badge className="text-[9px] bg-zinc-100 text-zinc-700">sin entrega</Badge>}
          {payload.sent === true && <Badge className="text-[9px] bg-green-50 text-green-800">notif. enviada</Badge>}
          {payload.sent === false && <Badge className="text-[9px] bg-red-50 text-red-700">notif. fallida</Badge>}
        </div>
        <div className="grid grid-cols-2 gap-2 text-[10px] text-zinc-600">
          {(payload.reminder_number != null) && (
            <p>Recordatorio: {payload.reminder_number}/{payload.max_reminders}</p>
          )}
          {(payload.silence_count != null) && <p>Silencios: {payload.silence_count}</p>}
          {payload.channel && <p>Canal: {payload.channel}</p>}
          {payload.channel_type && <p>Canal notif: {payload.channel_type}</p>}
          {payload.user_email && <p>Destino: {payload.user_email}</p>}
          {payload.address_masked && <p>Contacto: {payload.address_masked}</p>}
          {payload.ticket_id && <p>Ticket: {payload.ticket_id}</p>}
          {eff.timeout_minutes != null && <p>Timeout: {eff.timeout_minutes} min</p>}
          {eff.max_reminders != null && <p>Max: {eff.max_reminders}</p>}
          {eff.on_max_action && <p>Accion: {eff.on_max_action}</p>}
          {delivery.reason && <p>Entrega: {delivery.reason}</p>}
          {delivery.method && <p>Metodo: {delivery.method}</p>}
        </div>
        {payload.config_sources && <JsonBlock data={payload.config_sources} />}
        <GenericFields
          ev={ev}
          exclude={[
            'payload', 'delivery', 'effective_config', 'config_sources',
            'reminder_number', 'max_reminders', 'silence_count', 'channel',
            'delivered', 'action', 'forced_after_silence', 'skill_injected',
            'agent_id', 'ticket_id', 'user_email', 'channel_type', 'address_masked', 'sent',
          ]}
        />
      </div>
    );
  }

  return <GenericFields ev={ev} />;
}
